import React, { FunctionComponent, useCallback, useState, createContext, useContext, useMemo } from 'react';
import { NavigatorState, navigatorReducer } from 'app/models/navigator/reducer';
import NavigationError from 'app/constants/NavigationError';
import { SyncState } from 'app/models/rtc/sync/constants';
import { INavigatorAction } from '@engoo/realtime-protocol/navigator';
import { SyncNavigatorDocumentSpec } from 'app/models/navigator/documentSpec';
import useDocumentManager from 'app/containers/rtc/DocumentProvider/useDocumentManager';
import { Stream } from '@engoo/realtime-protocol/stream';
import { IMetadata } from 'app/services/rtc/sync/ActionStream';

export interface ISyncNavigatorContext {
  documentState: NavigatorState;
  syncState: SyncState;
  metadata: IMetadata | undefined;
  error?: NavigationError;
  addNavigationChange: (change: INavigatorAction) => void;
  setError: (error: NavigationError | undefined) => void;
  isBlocked: boolean;
}

const SyncNavigatorContext = createContext<ISyncNavigatorContext>({
  documentState: navigatorReducer(),
  syncState: SyncState.closed,
  metadata: undefined,
  isBlocked: false,
  error: undefined,
  addNavigationChange: () => {},
  setError: () => {},
});

export const SyncNavigatorRawProvider = SyncNavigatorContext.Provider;

export const useSyncNavigator = () => useContext(SyncNavigatorContext);

export const SyncNavigatorProvider: FunctionComponent = ({ children }) => {
  const {
    documentState = navigatorReducer(),
    syncState,
    metadata,
    manager,
    isBlocked,
  } = useDocumentManager(Stream.Navigator, SyncNavigatorDocumentSpec);

  const [error, setError] = useState<NavigationError | undefined>(undefined);


  const addNavigationChange = useCallback(
    (change: INavigatorAction) => manager?.addLocalActionWithResult(change),
    [manager],
  );

  const context = useMemo(() => ({
    documentState,
    syncState,
    metadata,
    isBlocked,
    error,
    addNavigationChange,
    setError,
  }), [
    documentState, syncState, metadata,
    addNavigationChange, error, setError, isBlocked,
  ]);

  return <SyncNavigatorContext.Provider value={context}>
    {children}
  </SyncNavigatorContext.Provider>;
};
